import { ALPHABET, GERMAN_PRINT_CAPITALS, type Glyph } from '@/play/letters/glyphs'
import { floorPlane, layGlyph, type GlyphPlane } from '@/play/letters/glyphPlane'
import { type Vec3 } from '@/play/ghost/strokePath'
import { hash2 } from '@/play/noise'
import { definePalette } from '@/play/palette'
import { voxelsByColor, worldCells } from '@/play/worldKit'
import { posKeyXYZ, type Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'

/**
 * Sand letters: a Montessori sand tray. A low wooden rim round a bed of fine sand four cells deep on
 * a dark wooden board; in the board a capital letter (German print, `glyphs.ts`) is laid in a warm
 * light gold. The sand hides it; writing in the sand with the spade uncovers it where the finger
 * follows the letter. The top grains over the letter may lean toward its colour (the hint). Pure
 * data, no document helpers: the reducer rebuilds a tray from its letter (events.ts).
 */

/** Stored in documents (`meta.experience.id`); never change it once the page has shipped. */
export const SAND_LETTERS_ID = 'sand-letters'
/**
 * Bump when the world or its rules change incompatibly. 2 (2026-10-15): finer cells (the letter 42
 * tall, was 26), a dark wooden board and a gold letter (were blue and raspberry), poured sand.
 */
export const SAND_LETTERS_VERSION = 2
export const SAND_LETTERS_BOUNDS: Position = { x: 68, y: 24, z: 64 }

/** The rim: three cells wide, standing two cells above the sand. */
const RIM_WIDTH = 3
/** Sand over the board: y = 1..SAND_DEPTH. */
export const SAND_DEPTH = 4
const RIM_TOP = SAND_DEPTH + 2
/** The letter's cap height in cells, and the half width of its strokes (a stroke about 6 cells wide). */
export const LETTER_HEIGHT = 42
export const LETTER_STROKE = 3.1
/** How far the top grains over the letter lean toward its colour (0: the letter is hidden). */
export const DEFAULT_HINT = 0.22

/** A warm light gold: bright on the dark board, saturated against the pale sand. */
export const LETTER_COLOR = 0xffc93c
const LETTER: VoxelData = { type: 'solid', color: LETTER_COLOR }
/** The board's planks (dark walnut), their darker grain, and the seams between planks. */
const BOARD: VoxelData = { type: 'solid', color: 0x5a3b26 }
const BOARD_COLORS = [0x4e321f, 0x634229, 0x3f2818, 0x33200f] as const
const BOARDS = BOARD_COLORS.map((color): VoxelData => ({ type: 'solid', color }))
const PLANK = 8
const RIM: VoxelData = { type: 'solid', color: 0x9b6b43 }
/** The bed's pale sands (014's), mixed by a hash so the bed is not one flat colour. */
const BED_COLORS = [0xe4cf9d, 0xe8d4a3, 0xedd9ab] as const
/** The poured sand: a touch warmer than the bed, so a fresh pile reads but belongs. */
export const POUR_COLOR = 0xdcc08a
/** The ghost drawing's pen: a bright sky blue, apart from the pale sand and the gold letter. */
export const GHOST: VoxelData = { type: 'solid', color: 0x3fb8ff }

/** The board, the letter, the rim, the sands, the ghost pen, the board's grain, the poured sand. Append groups, never reorder. */
export const SAND_LETTERS_PALETTE = definePalette({
  floor: BOARD,
  letter: LETTER,
  rim: RIM,
  sands: BED_COLORS.map((color): VoxelData => ({ type: 'sand', color })),
  ghost: GHOST,
  boards: BOARDS,
  pour: { type: 'sand', color: POUR_COLOR } as VoxelData,
})

/** Letters in the order "next letter" walks them: the alphabet, round again after Z. */
export const LETTERS = ALPHABET
export const letterIndex = (i: number) => ((Math.floor(i) % LETTERS.length) + LETTERS.length) % LETTERS.length
export const glyphAt = (i: number): Glyph => GERMAN_PRINT_CAPITALS.glyphs[LETTERS[letterIndex(i)]]

/** The tray's interior centre (cell centres at integers). */
const CX = (SAND_LETTERS_BOUNDS.x - 1) / 2
const CZ = (SAND_LETTERS_BOUNDS.z - 1) / 2

/** The tray's centre column (the pour's preview while sizing it). */
export const TRAY_CENTER = { x: Math.round(CX), z: Math.round(CZ) }

/** Letter `i`'s plane at height y: upright from the front, centred in the tray (the tracer maps onto it). */
export function letterPlane(i: number, y = 0): GlyphPlane {
  return floorPlane(glyphAt(i), CX, y, CZ, LETTER_HEIGHT)
}

/** Letter `i`'s strokes in writing order on a horizontal plane at height y, centred in the tray. */
export function letterStrokes(i: number, y = 0): Vec3[][] {
  return layGlyph(glyphAt(i), letterPlane(i, y))
}

/** The board under the sand: planks along x, a darker grain here and there, a seam between planks. */
function boardAt(x: number, z: number): VoxelData {
  const plank = Math.floor(z / PLANK)
  if (z % PLANK === 0) return BOARDS[3]
  const g = hash2(Math.floor(x / 3) + plank * 31, plank * 7 + (z % PLANK > PLANK / 2 ? 1 : 0))
  if (g < 0.22) return BOARDS[2]
  const tone = hash2(plank, 11)
  return tone < 0.34 ? BOARDS[0] : tone < 0.67 ? BOARD : BOARDS[1]
}

/** Distance in the floor plane from (x, z) to the nearest stroke segment. */
function distanceToStrokes(strokes: Vec3[][], x: number, z: number): number {
  let best = Infinity
  for (const s of strokes) {
    if (s.length === 1) best = Math.min(best, Math.hypot(x - s[0].x, z - s[0].z))
    for (let k = 1; k < s.length; k++) {
      const a = s[k - 1], b = s[k]
      const dx = b.x - a.x, dz = b.z - a.z
      const len2 = dx * dx + dz * dz
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / len2)) : 0
      best = Math.min(best, Math.hypot(x - (a.x + dx * t), z - (a.z + dz * t)))
    }
  }
  return best
}

/** The letter's floor columns (index x * Z + z), per letter, built once each. */
const masks = new Map<number, Uint8Array>()
export function letterMask(i: number): Uint8Array {
  const at = letterIndex(i)
  let mask = masks.get(at)
  if (mask) return mask
  const { x: X, z: Z } = SAND_LETTERS_BOUNDS
  const strokes = letterStrokes(at)
  mask = new Uint8Array(X * Z)
  for (let x = 0; x < X; x++) for (let z = 0; z < Z; z++) if (distanceToStrokes(strokes, x, z) <= LETTER_STROKE) mask[x * Z + z] = 1
  masks.set(at, mask)
  return mask
}

const mix = (a: number, b: number, t: number) => {
  const ch = (s: number) => Math.round(((a >> s) & 255) * (1 - t) + ((b >> s) & 255) * t)
  return (ch(16) << 16) | (ch(8) << 8) | ch(0)
}
const sand = voxelsByColor('sand')

/**
 * The tray for letter `i`: the board with the letter in it, the rim, and the sand bed, flat and at
 * rest. With `hint` > 0 the top grains over the letter lean that far toward the letter's colour.
 */
export function sandLettersWorldCells(i = 0, hint = DEFAULT_HINT, bounds: Position = SAND_LETTERS_BOUNDS) {
  const { x: X, z: Z } = bounds
  const mask = letterMask(i)
  const t = Math.max(0, Math.min(1, hint))
  const w = worldCells(bounds)
  w.floor(0, (x, z) => (mask[x * Z + z] ? LETTER : boardAt(x, z)))
  w.columns(
    (x, z) => (x < RIM_WIDTH || z < RIM_WIDTH || x >= X - RIM_WIDTH || z >= Z - RIM_WIDTH ? RIM_TOP : SAND_DEPTH),
    (x, y, z, top) => {
      if (top === RIM_TOP) return RIM
      const color = BED_COLORS[Math.min(BED_COLORS.length - 1, Math.floor(hash2(x * 7 + y, z * 3 - y) * BED_COLORS.length))]
      return sand(y === SAND_DEPTH && t > 0 && mask[x * Z + z] ? mix(color, LETTER_COLOR, t) : color)
    },
    { from: 1 },
  )
  return w.cells()
}

/**
 * Which letter a tray holds, read from its floor (protected, so only a new tray, its undo or a fresh
 * start changes it): the letter whose mask is exactly the floor's gold cells, else null.
 */
export function trayLetter(get: (key: number) => VoxelData | undefined, bounds: Position = SAND_LETTERS_BOUNDS): number | null {
  const { x: X, z: Z } = bounds
  const found = new Uint8Array(X * Z)
  let count = 0
  for (let x = 0; x < X; x++) {
    for (let z = 0; z < Z; z++) {
      const v = get(posKeyXYZ(x, 0, z))
      if (v && v.type === 'solid' && v.color === LETTER_COLOR) { found[x * Z + z] = 1; count++ }
    }
  }
  if (count === 0) return null
  for (let i = 0; i < LETTERS.length; i++) {
    const mask = letterMask(i)
    let same = true
    for (let k = 0; k < mask.length && same; k++) if (mask[k] !== found[k]) same = false
    if (same) return i
  }
  return null
}
